import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { moneyTight } from '../../utils/region';
import { brandOr, firmLabelOr } from '../../utils/brand';

// Marketing-role portal — MOBILE-FIRST and fully READ-ONLY. A marketing person sees, on their phone:
//   • their clients (basic info only — no editing, deleting, or creating anything)
//   • their own finance share (default whole year; switchable to this month / last month / custom)
// There is deliberately no create/edit/delete affordance anywhere.

const fmtDate = (d?: string) => { if (!d) return ''; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d); return m ? `${m[3]}/${m[2]}/${m[1]}` : d; };
const firmsOf = (s?: string) => { try { return (JSON.parse(s || '[]') as string[]).map(f => f === 'legal' ? 'Legal' : f === 'accounting' ? 'Accounting' : f); } catch { return []; } };

const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16 };
const label: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-tertiary)', letterSpacing: '.03em' };
const value: React.CSSProperties = { fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2, wordBreak: 'break-word' };

function KV({ k, v }: { k: string; v?: any }) {
  if (v === undefined || v === null || v === '') return null;
  return <div><div style={label}>{k}</div><div style={value}>{v}</div></div>;
}

// ── Client basic-info detail (read-only) ─────────────────────────────────────
function ClientDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.marketingClient(id).then(setData).catch(() => {}).finally(() => setLoading(false)); }, [id]);
  const c = data?.client; const people: any[] = data?.people || [];
  const directors = people.filter(p => p.role !== 'shareholder');
  const shareholders = people.filter(p => p.role !== 'director');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 40 }}>
      <button onClick={onBack} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' }}>
        <i className="ti ti-arrow-left" />{t('mkp_back')}
      </button>
      {loading ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : !c ? null : (
        <>
          <div style={card}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 2 }}>{c.company_name || c.client_name}</div>
            {c.company_name_th && <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 12 }}>{c.company_name_th}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
              <KV k={t('cd_contact')} v={c.client_name} />
              <KV k={t('cd_tax_id')} v={c.tax_id} />
              <KV k={t('cd_firms')} v={firmsOf(c.firms).join(' · ')} />
              <KV k={t('cd_type')} v={c.client_type} />
              <KV k={t('cd_vat')} v={c.has_vat ? t('cd_yes') : (c.has_vat === 0 ? t('cd_no') : '')} />
              <KV k="Email" v={c.email} />
              <KV k={t('cd_phone')} v={c.phone} />
              <KV k={t('cd_acct_status')} v={firmsOf(c.acct_status).length ? firmsOf(c.acct_status).join(' · ') : c.acct_status} />
              <KV k={t('cd_founding')} v={fmtDate(c.founding_date)} />
            </div>
            {(c.registered_address_th || c.registered_address || c.address) && (
              <div style={{ marginTop: 12 }}><KV k={t('cd_address')} v={c.registered_address_th || c.registered_address || c.address} /></div>
            )}
          </div>

          {directors.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 10 }}><i className="ti ti-gavel" style={{ marginRight: 6, color: 'var(--accent)' }} />{t('cd_directors')} ({directors.length})</div>
              {directors.map(p => (
                <div key={p.id} style={{ padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700 }}>{p.name}{p.is_foreign ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#B45309' }}>{t('cd_foreign')}</span> : null}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>{[p.nationality, p.id_number, p.signing_authority].filter(Boolean).join(' · ')}</div>
                </div>
              ))}
            </div>
          )}
          {shareholders.length > 0 && (
            <div style={card}>
              <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 10 }}><i className="ti ti-chart-pie" style={{ marginRight: 6, color: 'var(--accent)' }} />{t('cd_shareholders')} ({shareholders.length})</div>
              {shareholders.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                  <div><div style={{ fontSize: 14.5, fontWeight: 700 }}>{p.name}</div><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{[p.nationality, p.id_number].filter(Boolean).join(' · ')}</div></div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{p.shareholding_pct ? `${p.shareholding_pct}%` : '—'}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Finance (read-only, period selectable; default whole year) ───────────────
function FinanceView() {
  const { t } = useTranslation();
  const y = new Date().getFullYear();
  const ym = (d: Date) => d.toISOString().slice(0, 10);
  const [period, setPeriod] = useState<'year' | 'this' | 'last' | 'custom'>('year');
  const [from, setFrom] = useState(`${y}-01-01`);
  const [to, setTo] = useState(`${y}-12-31`);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const range = (p: typeof period): [string, string] => {
    const now = new Date();
    if (p === 'year') return [`${y}-01-01`, `${y}-12-31`];
    if (p === 'this') return [ym(new Date(now.getFullYear(), now.getMonth(), 1)), ym(new Date(now.getFullYear(), now.getMonth() + 1, 0))];
    if (p === 'last') return [ym(new Date(now.getFullYear(), now.getMonth() - 1, 1)), ym(new Date(now.getFullYear(), now.getMonth(), 0))];
    return [from, to];
  };
  useEffect(() => {
    const [f, tt] = range(period);
    if (period !== 'custom') { setFrom(f); setTo(tt); }
    setLoading(true);
    api.financeMine(f, tt).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period, period === 'custom' ? from : '', period === 'custom' ? to : '']); // eslint-disable-line

  const chip = (p: typeof period, lbl: string) => (
    <button onClick={() => setPeriod(p)} style={{ padding: '8px 14px', borderRadius: 999, border: '1px solid', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', borderColor: period === p ? 'var(--accent)' : 'var(--border-strong)', background: period === p ? 'var(--accent)' : 'transparent', color: period === p ? '#fff' : 'var(--text-secondary)' }}>{lbl}</button>
  );
  const firms: any[] = data?.firms || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 40 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {chip('year', t('mkp_year'))}{chip('this', t('mkp_this_month'))}{chip('last', t('mkp_last_month'))}{chip('custom', t('mkp_custom'))}
      </div>
      {period === 'custom' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ padding: '9px 11px', fontSize: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }} />
          <span style={{ color: 'var(--text-tertiary)' }}>→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={{ padding: '9px 11px', fontSize: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }} />
        </div>
      )}
      {loading ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
        : firms.length === 0 ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13.5 }}>{t('mkp_no_finance')}</div>
        : firms.map((f: any) => (
          <div key={f.firm} style={card}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>{f.firm === 'legal' ? firmLabelOr('legal', t('cs_kind_legal')) : firmLabelOr('accounting', t('cs_kind_accounting'))}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div><div style={label}>{t('fr_receipted')}</div><div style={{ ...value, fontSize: 20, color: 'var(--accent)' }}>{moneyTight(f.receipted)}</div></div>
              <div><div style={label}>{t('fr_invoiced_quoted')}</div><div style={{ ...value, fontSize: 20 }}>{moneyTight(f.invoicedQuoted)}</div></div>
              <div><div style={label}>{t('fr_invoiced')}</div><div style={value}>{moneyTight(f.invoiced)}</div></div>
              <div><div style={label}>{t('fr_quoted')}</div><div style={value}>{moneyTight(f.quoted)}</div></div>
            </div>
          </div>
        ))}
      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', textAlign: 'center', lineHeight: 1.6 }}>{t('mkp_finance_note')}</div>
    </div>
  );
}

export default function MarketingPortal() {
  const { t } = useTranslation();
  const { me, logout } = useStore();
  const [view, setView] = useState<'clients' | 'finance'>('clients');
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string>('');
  const [q, setQ] = useState('');

  useEffect(() => { api.marketingClients().then((d: any) => setClients(d.rows || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  const filtered = clients.filter(c => !q.trim() || `${c.company_name} ${c.client_name}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', position: 'sticky', top: 0, zIndex: 10 }}>
        <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 26, height: 26, objectFit: 'contain' }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.1 }}>{me?.name || 'Marketing'}</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}><i className="ti ti-eye" style={{ fontSize: 11 }} /> {t('mkp_readonly')}</div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={logout} title={t('mkp_signout')} style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}><i className="ti ti-logout" /></button>
      </header>

      {/* bottom-nav-style tab switch (thumb-friendly) */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)', position: 'sticky', top: 51, zIndex: 9 }}>
        {(['clients', 'finance'] as const).map(v => (
          <button key={v} onClick={() => { setView(v); setOpen(''); }} style={{ flex: 1, padding: '11px 0', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 800, cursor: 'pointer', background: view === v ? 'var(--accent)' : 'var(--bg-app)', color: view === v ? '#fff' : 'var(--text-secondary)' }}>
            <i className={`ti ti-${v === 'clients' ? 'users' : 'report-money'}`} style={{ marginRight: 6 }} />{v === 'clients' ? t('mkp_my_clients') : t('mkp_finance')}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16, maxWidth: 620, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {view === 'finance' ? <FinanceView />
          : open ? <ClientDetail id={open} onBack={() => setOpen('')} />
          : loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBottom: 40 }}>
              <div style={{ position: 'relative' }}>
                <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: 'var(--text-tertiary)' }} />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('mkp_search')} style={{ width: '100%', padding: '12px 12px 12px 36px', fontSize: 15, border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('mkp_client_count', { n: filtered.length })}</div>
              {filtered.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13.5 }}>{t('mkp_no_clients')}</div>
                : filtered.map(c => (
                  <button key={c.id} onClick={() => setOpen(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '14px 15px', border: '1px solid var(--border)', borderRadius: 14, background: 'var(--bg-surface)', cursor: 'pointer' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-bg, #ECFDF5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><i className="ti ti-building" style={{ fontSize: 19, color: 'var(--accent)' }} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || c.client_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>{[firmsOf(c.firms).join(' · '), c.tax_id].filter(Boolean).join(' · ')}{c.percent ? ` · ${c.percent}%` : ''}</div>
                    </div>
                    <i className="ti ti-chevron-right" style={{ fontSize: 18, color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  </button>
                ))}
            </div>
          )}
      </div>
    </div>
  );
}
